/**
 * 教室 API（org-campus change 2-7，ADM-28）。
 *
 * 对齐后端 org-campus change 的 REST 契约：
 * - GET /classrooms -> Paginated<ClassroomItem>（筛选 + 分页）
 * - GET /classrooms/:id -> ClassroomItem
 * - POST /classrooms -> ClassroomItem（新建）
 * - PATCH /classrooms/:id -> ClassroomItem（编辑，含乐观锁 version）
 * - POST /classrooms/:id/disable -> ClassroomItem（停用）
 * - POST /classrooms/:id/enable -> ClassroomItem（启用）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 教室状态。 */
export type ClassroomStatus = 'AVAILABLE' | 'DISABLED';

/** 教室视图（列表项与详情共用，对齐后端 ClassroomResponseDto）。 */
export interface ClassroomItem {
  id: string;
  /** 所属校区 ID */
  campusId: string;
  /** 校区名称（后端 join 派生） */
  campusName: string;
  /** 教室名称（同校区唯一） */
  name: string;
  /** 容量（>= 1） */
  capacity: number;
  status: ClassroomStatus;
  /** 乐观锁版本号 */
  version: number;
  /** 未来占用课次数（一期占位 0，design D6 待回填真实 COUNT） */
  futureSessionCount: number;
  /** 更新时间（ISO 8601） */
  updatedAt: string;
}

/** 分页响应。 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** 教室列表查询参数。 */
export interface ClassroomListQuery {
  campusId?: string;
  name?: string;
  status?: ClassroomStatus;
  minCapacity?: number;
  maxCapacity?: number;
  page?: number;
  pageSize?: number;
}

/** 新建教室请求体。 */
export interface CreateClassroom {
  campusId: string;
  name: string;
  capacity: number;
}

/** 编辑教室请求体（含乐观锁 version）。 */
export interface UpdateClassroom {
  name?: string;
  capacity?: number;
  version: number;
}

function buildListQuery(query: ClassroomListQuery): string {
  const params = new URLSearchParams();
  if (query.campusId) params.set('campusId', query.campusId);
  if (query.name) params.set('name', query.name);
  if (query.status) params.set('status', query.status);
  if (query.minCapacity !== undefined)
    params.set('minCapacity', String(query.minCapacity));
  if (query.maxCapacity !== undefined)
    params.set('maxCapacity', String(query.maxCapacity));
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.classrooms.list}?${qs}` : ApiPaths.classrooms.list;
}

async function listClassrooms(
  query: ClassroomListQuery = {},
): Promise<Paginated<ClassroomItem>> {
  return http.get<Paginated<ClassroomItem>>(buildListQuery(query));
}

async function getClassroom(id: string): Promise<ClassroomItem> {
  return http.get<ClassroomItem>(ApiPaths.classrooms.detail(id));
}

async function createClassroom(dto: CreateClassroom): Promise<ClassroomItem> {
  return http.post<ClassroomItem>(ApiPaths.classrooms.create, dto);
}

async function updateClassroom(
  id: string,
  dto: UpdateClassroom,
): Promise<ClassroomItem> {
  return http.patch<ClassroomItem>(ApiPaths.classrooms.update(id), dto);
}

async function disableClassroom(id: string): Promise<ClassroomItem> {
  return http.post<ClassroomItem>(ApiPaths.classrooms.disable(id), {});
}

async function enableClassroom(id: string): Promise<ClassroomItem> {
  return http.post<ClassroomItem>(ApiPaths.classrooms.enable(id), {});
}

/** 教室 API 唯一对外入口。 */
export const classroomsApi = {
  listClassrooms,
  getClassroom,
  createClassroom,
  updateClassroom,
  disableClassroom,
  enableClassroom,
};
